import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Brain, Trash2, Loader2, Info } from "lucide-react";
import { api } from "@/lib/api";

/**
 * AI Memory card — renders Obondo's persisted cross-session memory.
 *
 * Mounted on the trader profile / settings page. Each item shows the
 * user message, Obondo's reply, and the timestamp. Individual delete
 * per item, or "Clear all" nuke.
 */
export default function AiMemoryCard() {
    const [items, setItems] = useState(null);
    const [busy, setBusy] = useState(false);

    async function load() {
        try {
            const { data } = await api.get("/chat/assistant/memory");
            setItems(data.items || []);
        } catch {
            setItems([]);
        }
    }
    useEffect(() => { load(); }, []);

    async function del(id) {
        setBusy(true);
        try {
            await api.delete(`/chat/assistant/memory/${id}`);
            setItems((prev) => prev.filter((i) => i.id !== id));
            toast.success("Memory item deleted.");
        } catch {
            toast.error("Delete failed.");
        } finally {
            setBusy(false);
        }
    }

    async function clearAll() {
        if (!window.confirm("Delete ALL of Obondo's memory of you? Can't be undone.")) return;
        setBusy(true);
        try {
            const { data } = await api.delete("/chat/assistant/memory");
            setItems([]);
            toast.success(`Cleared ${data.deleted_count} memory item(s).`);
        } catch {
            toast.error("Clear failed.");
        } finally {
            setBusy(false);
        }
    }

    if (items === null) {
        return (
            <div className="border border-border bg-card rounded-lg p-4 flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading Obondo memory…
            </div>
        );
    }

    return (
        <div className="border border-border bg-card rounded-lg p-4 sm:p-5" data-testid="ai-memory-card">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Brain className="w-5 h-5 text-primary" />
                    <h3 className="font-bold text-base">What Obondo remembers about you</h3>
                </div>
                {items.length > 0 && (
                    <button
                        onClick={clearAll}
                        disabled={busy}
                        className="text-xs text-red-500 underline hover:text-red-600 disabled:opacity-50"
                        data-testid="ai-memory-clear"
                    >
                        Clear all
                    </button>
                )}
            </div>

            <div className="text-xs text-muted-foreground mb-4 flex gap-2 items-start">
                <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span>
                    Obondo persists a small trace of your last conversations so it can pick up where you left off
                    across sessions. You control what stays. Nothing is shared with other users.
                </span>
            </div>

            {items.length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground">
                    No memory yet — start a conversation with Obondo and future chats will build up context here.
                </div>
            ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                    {items.map((it) => (
                        <div key={it.id}
                            className="border border-border rounded-md p-3 bg-muted/30 flex items-start gap-2"
                            data-testid={`ai-memory-item-${it.id}`}
                        >
                            <div className="flex-1 min-w-0">
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                                    {it.ts?.slice(0, 16).replace("T", " ")} · session {it.session_id?.slice(0, 8)}
                                </div>
                                <div className="text-xs mb-1"><b>You:</b> {it.user_msg}</div>
                                <div className="text-xs text-muted-foreground"><b>Obondo:</b> {it.assistant_msg}</div>
                            </div>
                            <button
                                onClick={() => del(it.id)}
                                disabled={busy}
                                className="text-muted-foreground hover:text-red-500 shrink-0"
                                title="Forget this exchange"
                                data-testid={`ai-memory-del-${it.id}`}
                            >
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
